import { useState, useEffect, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { Text, Money, Button } from '@belong/ui';
import Checkbox from 'components/Checkbox/Checkbox';
import ModalV2 from 'components/Modal/ModalV2/ModalV2';
import Slider from 'components/v2/Slider/Slider';
import Tooltip from 'components/Tooltip/Tooltip';
import ModalLayout from 'layouts/ModalLayout/ModalLayout';
import { PaymentPreference, PaymentPriority } from 'models/enums/typings';
import PropTypes from 'prop-types';
import {
  simulateSplitPayments,
  createFinancePlan,
  createSplitItPaymentFirst,
} from 'store/redux/accounts/actions';
import {
  selectSimulatePayments,
  selectBalances,
} from 'store/redux/accounts/selectors';
import { selectSettings } from 'store/redux/settings';
import { selectFullUser } from 'store/redux/user/selectors';
import { getSplitItUnavailableMessage } from 'pages/Accounts/Components/split-it-unavailable-message';
import { DisclosureModal } from '../disclosure-modal/disclosure-modal';

const SLIDER_STEP = 1;

// reactjs-popup renders tooltip content at z-index 999 by default, which sits
// behind ModalV2 (z-index up to 999999). Lift it above the modal so the
// tooltip is actually visible on hover/click.
const TOOLTIP_CONTENT_STYLE = { zIndex: 1000001 };

// The consumer API rejects 4xx with an array of `{ message }`, but 5xx (and
// network/parse failures) don't follow that shape, so `err[0].message` throws.
// Extract a message defensively and fall back to a generic one.
function getErrorMessage(
  err,
  fallback = 'Something went wrong. Please try again.',
) {
  if (Array.isArray(err) && err[0]?.message) return err[0].message;
  if (err?.message) return err.message;
  return fallback;
}

function SplitPaymentModal({
  loading,
  onHide,
  balance,
  simulateSplitPayments: simulateSplitPaymentsAction,
  simulation,
  createFinancePlan: createFinancePlanAction,
  createSplitItPaymentFirst: createSplitItPaymentFirstAction,
  balances,
  items,
  handleShowFinanceSuccessModal,
  billingInfo,
  ...rest
}) {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [simulatorLoaded, setSimulatorLoaded] = useState(false);
  const [monthlyInstallments, setMonthlyInstallments] = useState(2);
  const [sliderTimeout, setSliderTimeout] = useState(null);
  const [selectedItems, setSelectedItems] = useState(() =>
    items.reduce(
      (obj, item) =>
        Object.assign(obj, { [item.sourceKey]: { ...item, selected: true } }),
      {},
    ),
  );
  const [allSelected, setAllSelected] = useState(true);
  const [showDisclosure, setShowDisclosure] = useState(false);

  const settings = useSelector(selectSettings);
  const interestRateValue =
    settings?.commerceSettings?.splitItSettings?.interestRateValue;

  const fullUser = useSelector(selectFullUser);
  // Payment-first balances are in-lease pro services, so the upfront charge
  // targets the member's Pro Services (Maintenance) account. Auto-split members
  // have no Maintenance primary (both are mutually exclusive), so fall back to
  // the payout primary — the same chain Pay Now uses for homeowners.
  const upfrontPaymentMethod =
    fullUser?.getPaymentMethodFor(
      PaymentPreference.Maintenance,
      PaymentPriority.Primary,
    ) ??
    fullUser?.getPaymentMethodFor(
      PaymentPreference.Payout,
      PaymentPriority.Primary,
    );

  // Payment-first Split It: when the simulation returns splitItPaymentFirst,
  // the backend owns every number (upfront, financed, installment options).
  // When absent, the legacy flow below stays untouched.
  const splitItPaymentFirst = simulation?.splitItPaymentFirst;
  const isPaymentFirst = Boolean(splitItPaymentFirst?.isInScope);
  const availableInstallmentCounts = isPaymentFirst
    ? splitItPaymentFirst.availableInstallmentCounts || []
    : [];
  const isSplitItUnavailable =
    isPaymentFirst &&
    (!splitItPaymentFirst.isSplitItAvailable ||
      availableInstallmentCounts.length === 0);
  const isSingleStop =
    isPaymentFirst && availableInstallmentCounts.length === 1;
  const sliderMax =
    isPaymentFirst && availableInstallmentCounts.length > 0
      ? availableInstallmentCounts[availableInstallmentCounts.length - 1]
      : 12;
  const sliderMin =
    isPaymentFirst && availableInstallmentCounts.length > 0
      ? // Single stop: keep a one-step range so the track renders full,
        // with the only valid value pinned at the right end.
        isSingleStop
        ? sliderMax - 1
        : availableInstallmentCounts[0]
      : 2;

  const balancesIds = useMemo(
    () =>
      Object.keys(selectedItems)
        .filter((key) => {
          return selectedItems[key].selected;
        })
        .map((key) => {
          const filteredBalances =
            selectedItems[key].sourceType === 'Balance'
              ? [selectedItems[key]]
              : selectedItems[key]?.balances || [];
          return [...filteredBalances];
        })
        .flat()
        .map((bal) => bal.sourceKey),
    [selectedItems],
  );

  // One key per confirmation attempt (same selection + installments), so a
  // double click or retry of the same attempt cannot create duplicate work.
  // The deps are not read inside: they force a fresh key per distinct attempt.
  const idempotencyKey = useMemo(
    () => crypto.randomUUID(),
    [balancesIds, monthlyInstallments],
  );

  function handleHide() {
    setSimulatorLoaded(false);
    setSelectedItems({});
    if (onHide) onHide();
  }

  function handleToggleAll(value) {
    setAllSelected(value);
    setSelectedItems((prevState) => {
      const newState = { ...prevState };
      Object.keys(prevState).forEach((key) => {
        newState[key].selected = value;
      });

      return newState;
    });
  }

  function handleToggle(value, item) {
    setSelectedItems((prevState) => {
      return {
        ...prevState,
        ...(value
          ? { [item.sourceKey]: { ...item, selected: true } }
          : { [item.sourceKey]: { selected: false } }),
      };
    });
  }

  function getTotalAmountSelected() {
    let total = 0;
    Object.values(selectedItems).forEach((item) => {
      if (item.selected) total += item.netBalance;
    });
    return Math.abs(total);
  }

  function getMonthlyTotal() {
    if (isPaymentFirst) {
      return splitItPaymentFirst.monthlyPayment ?? '';
    }

    if (simulation.finance) {
      return (
        simulation.finance.monthlyAmount +
        simulation.finance.monthlyInterestAmount
      );
    }

    return '';
  }

  function getAprValue() {
    if (isPaymentFirst && splitItPaymentFirst.apr != null) {
      return splitItPaymentFirst.apr;
    }

    return interestRateValue;
  }

  function getMonthlyInterestAmount() {
    if (isPaymentFirst) {
      // financeSimulation exposes the total interestAmount, not a monthly
      // figure — divide like the legacy flow does.
      const financeSimulation = splitItPaymentFirst.financeSimulation;
      if (
        financeSimulation?.interestAmount &&
        financeSimulation?.installments
      ) {
        return (
          financeSimulation.interestAmount / financeSimulation.installments
        );
      }
      return 0;
    }

    return simulation?.finance?.monthlyInterestAmount ?? 0;
  }

  function getTotalSelected() {
    const selected = Object.values(selectedItems).filter((item) => {
      return item.selected;
    });

    if (selected) return selected.length;

    return 0;
  }

  async function handleSimulation(installments) {
    const buildSimulation = {
      installments,
      startDate: billingInfo?.billGenerationOn,
      balanceUniqueIds: balancesIds,
    };

    if (buildSimulation.balanceUniqueIds.length === 0) {
      setIsLoading(false);
      setSimulatorLoaded(true);
      return;
    }
    setError(null);
    try {
      await simulateSplitPaymentsAction(buildSimulation);
      setIsLoading(false);
    } catch (err) {
      setError(getErrorMessage(err));
      setIsLoading(false);
      console.error(err);
    }
  }

  async function handleCreateFinancePlan() {
    setIsLoading(true);
    setError(null);

    if (isPaymentFirst && splitItPaymentFirst.canStartRequiredPayment) {
      if (!upfrontPaymentMethod?.accountId) {
        setError(
          'Add a Pro Services payment method to pay the upfront amount.',
        );
        setIsLoading(false);
        return;
      }

      try {
        const response = await createSplitItPaymentFirstAction({
          installments: monthlyInstallments,
          balanceUniqueIds: balancesIds,
          paymentAccountUniqueId: upfrontPaymentMethod.accountId,
          idempotencyKey,
        });
        if (response?.intentId) {
          onHide();
          if (handleShowFinanceSuccessModal) {
            handleShowFinanceSuccessModal();
          }
        }
        setIsLoading(false);
      } catch (err) {
        setError(getErrorMessage(err));
        setIsLoading(false);
        console.error(err);
      }
      return;
    }

    try {
      const financePlan = {
        installments: monthlyInstallments,
        startDate: billingInfo.billGenerationOn,
        balanceUniqueIds: balancesIds,
      };
      const response = await createFinancePlanAction(financePlan);
      if (response.finance) {
        onHide();
        if (handleShowFinanceSuccessModal) {
          handleShowFinanceSuccessModal();
        }
      }
      setIsLoading(false);
    } catch (err) {
      setError(getErrorMessage(err));
      setIsLoading(false);
      console.error(err);
    }
  }

  function handleSliderchange(value) {
    setMonthlyInstallments(value);

    if (sliderTimeout) clearTimeout(sliderTimeout);
    setSliderTimeout(
      setTimeout(async () => {
        if (getTotalSelected() === 0) return;
        setIsLoading(true);
        handleSimulation(value);
      }, 300),
    );
  }

  // const hideDisclosure = useCallback(() => setShowDisclosure(false), [])

  useEffect(() => () => clearTimeout(sliderTimeout), [sliderTimeout]);

  // If the selected installment count is no longer offered for the current
  // selection, snap to the highest available option and re-simulate.
  useEffect(() => {
    if (!isPaymentFirst || availableInstallmentCounts.length === 0) return;
    if (!availableInstallmentCounts.includes(monthlyInstallments)) {
      const highestAvailable =
        availableInstallmentCounts[availableInstallmentCounts.length - 1];
      setMonthlyInstallments(highestAvailable);
      handleSimulation(highestAvailable);
    }
  }, [splitItPaymentFirst]);

  useEffect(() => {
    const simulatePayment = async () => {
      const ids = Object.keys(selectedItems)
        .map((key) => {
          return selectedItems[key].sourceType === 'Balance'
            ? selectedItems[key]
            : [...selectedItems[key].balances];
        })
        .flat()
        .map((bal) => bal.sourceKey);

      const buildSimulation = {
        installments: monthlyInstallments,
        startDate: billingInfo.billGenerationOn,
        balanceUniqueIds: ids,
      };

      if (buildSimulation.balanceUniqueIds.length === 0) {
        setIsLoading(false);
        setSimulatorLoaded(true);
        return;
      }

      try {
        await simulateSplitPaymentsAction(buildSimulation);
        setSimulatorLoaded(true);
        setIsLoading(false);
      } catch (err) {
        setSimulatorLoaded(true);
        setIsLoading(false);
        console.error(err);
      }
    };
    if (rest.show && selectedItems && !simulatorLoaded && !isLoading) {
      simulatePayment();
      setIsLoading(true);
    }
  }, [rest, selectedItems]);

  useEffect(() => {
    if (simulatorLoaded) {
      handleSimulation(monthlyInstallments);
      if (getTotalSelected() === Object.keys(selectedItems).length) {
        setAllSelected(true);
      } else {
        setAllSelected(false);
      }

      if (getTotalSelected() > 0) handleSimulation(monthlyInstallments);
    }
  }, [selectedItems, simulatorLoaded]);

  return (
    <>
      <ModalV2
        {...rest}
        noPadding
        showSpinner={loading || isLoading}
        onHide={handleHide}
        className=""
      >
        <ModalLayout fixedBottom>
          <ModalLayout.Content className="p-0">
            <ModalLayout.Section>
              <ModalLayout.Title className="mb-0">
                Split Payments Into <br /> Monthly Installments
              </ModalLayout.Title>
            </ModalLayout.Section>
            <ModalLayout.Section className="mt-0">
              <div className="flex px-sm pt-xl pb-xs md:pb-xl">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th width="60%">
                        <div className="flex">
                          <Checkbox
                            alignCenter
                            value={allSelected}
                            useOnChangeFormToHandle
                            label="All Items"
                            onSelect={(val) => handleToggleAll(val)}
                            className="text-h3-sm md:text-h3-xl"
                          />
                        </div>
                      </th>
                      <th className="flex justify-end ">
                        <Text
                          className="absolute top-[105px] self-end text-body md:static"
                          fontWeight="semibold"
                        >
                          Remaining Balance ($)
                        </Text>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan="2" className="py-sm">
                        <div className="border-0 border-t border-dashed border-gray" />
                      </td>
                    </tr>
                    {items.map((item, index) => {
                      const isLastItem = index === items.length - 1;
                      return (
                        <tr
                          key={item.sourceKey}
                          className={item.selected ? 'selected' : ''}
                        >
                          <th
                            scope="row"
                            width="60%"
                            className={isLastItem ? '' : 'pb-sm'}
                          >
                            <Checkbox
                              alignCenter
                              value={selectedItems[item.sourceKey]?.selected}
                              useOnChangeFormToHandle
                              label={item.description}
                              onSelect={(val) => handleToggle(val, item)}
                            />
                          </th>
                          <td
                            className={`flex justify-end ${
                              isLastItem ? '' : 'pb-sm'
                            }`}
                          >
                            <Money
                              format="DOLLARS_WITH_CENTS"
                              variant="body"
                              fontWeight="semibold"
                              value={item.netBalance}
                            />
                          </td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td colSpan="2" className="py-sm">
                        <div className="border-0 border-t border-dashed border-gray" />
                      </td>
                    </tr>
                    <tr>
                      <th className="text-left">
                        <Text
                          className="ml-2xs text-h3-sm md:text-h3-xl"
                          fontWeight="semibold"
                        >
                          {getTotalSelected()} Selected
                        </Text>
                      </th>
                      <td className="flex justify-end">
                        <Money
                          format="DOLLARS_WITH_CENTS"
                          className="text-h3-sm md:text-h3-xl"
                          fontWeight="semibold"
                          value={getTotalAmountSelected()}
                        />{' '}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-xl min-h-[164px] w-full border border-solid border-green bg-light-green/5 px-sm py-xl md:mt-0">
                {isSplitItUnavailable || error ? (
                  <div className="flex min-h-[116px] items-center justify-center text-center">
                    <Text variant="p1">
                      {getSplitItUnavailableMessage(
                        error ||
                          splitItPaymentFirst.message ||
                          splitItPaymentFirst.ineligibilityReason,
                      )}
                    </Text>
                  </div>
                ) : (
                  <div>
                    {isPaymentFirst && (
                      <>
                        <div className="flex flex-col gap-xs">
                          {splitItPaymentFirst.requiredPaymentAmount > 0 && (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-xs">
                                <Text variant="body" fontWeight="semibold">
                                  Due Today (Upfront)
                                </Text>
                                <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE}>
                                  The maximum financeable amount equals monthly
                                  rent times months remaining. Any balance above
                                  that cap must be paid upfront today.
                                </Tooltip>
                              </div>
                              <Money
                                format="DOLLARS_WITH_CENTS"
                                variant="body"
                                fontWeight="semibold"
                                value={
                                  -Math.abs(
                                    splitItPaymentFirst.requiredPaymentAmount,
                                  )
                                }
                              />
                            </div>
                          )}
                          <div className="flex items-center justify-between">
                            <Text variant="body" fontWeight="semibold">
                              Splitting
                            </Text>
                            <Money
                              format="DOLLARS_WITH_CENTS"
                              variant="body"
                              fontWeight="semibold"
                              value={
                                -Math.abs(splitItPaymentFirst.financedPrincipal)
                              }
                            />
                          </div>
                        </div>
                        <div className="my-xl border-0 border-t border-dashed border-gray" />
                      </>
                    )}
                    <div className="mb-xs flex flex-col items-center text-center">
                      <div className="mb-xs flex">
                        <Money
                          className=""
                          format="DOLLARS_WITH_CENTS"
                          variant="h3"
                          fontWeight="semibold"
                          value={
                            getTotalSelected() === 0 ? 0 : getMonthlyTotal()
                          }
                        />
                        <Text
                          variant="body"
                          fontWeight="semibold"
                          className="relative top-2xs mr-xs md:top-[7px]"
                        >
                          /Mo.
                        </Text>
                      </div>
                      <div className="flex">
                        <Text variant="p1" className="mr-xs text-center">
                          Including {getAprValue()}% APR ($
                          {getTotalSelected() === 0
                            ? 0
                            : getMonthlyInterestAmount().toLocaleString('en', {
                                useGrouping: false,
                                minimumFractionDigits: 2,
                              })}
                          /Mo.) • No Additional Fees <br />
                          Due On The 1st Of Each Month{' '}
                        </Text>
                      </div>
                    </div>
                    <div className="mx-auto w-full max-w-[503px]">
                      <Slider
                        max={sliderMax}
                        min={sliderMin}
                        disabled={isSingleStop}
                        onChange={handleSliderchange}
                        step={SLIDER_STEP}
                        value={monthlyInstallments}
                        renderBottom={(val) => (
                          <div className="my-sm flex justify-center md:hidden">
                            <Text variant="p" fontWeight="semibold">
                              {val} Monthly Installments
                            </Text>
                          </div>
                        )}
                        renderTop={(val) => (
                          <>
                            <div className="my-sm hidden  justify-center md:flex">
                              <Text variant="p" fontWeight="semibold">
                                {val} Monthly Installments
                              </Text>
                            </div>
                            <div className="mb-xs flex md:hidden">
                              <div>
                                <Text variant="p1">{sliderMin} Months</Text>
                              </div>
                              <div className="ml-auto flex items-center gap-xs">
                                <Text variant="p1">{sliderMax} Months</Text>
                                <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE}>
                                  Split your balance across the time remaining
                                  on your lease.
                                </Tooltip>
                              </div>
                            </div>
                          </>
                        )}
                        renderLeft={() => (
                          <div className="mr-xs hidden md:flex">
                            <Text variant="p1">{sliderMin} Months</Text>
                          </div>
                        )}
                        renderRight={() => (
                          <div className="ml-xs hidden items-center gap-xs md:flex">
                            <Text variant="p1">{sliderMax} Months</Text>
                            <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE}>
                              Split your balance across the time remaining on
                              your lease.
                            </Tooltip>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                )}
              </div>
            </ModalLayout.Section>
            <ModalLayout.BottomBar
              className="mt-xl w-full flex-col md:m-0 md:flex-row"
              ctaProps={{
                onClick: () => {
                  handleCreateFinancePlan();
                },
                children: <div>Split It!</div>,
                disabled:
                  getTotalSelected() === 0 || isSplitItUnavailable || !!error,
              }}
              noMarginOrPaddingOnMobileForButton
              leftSideNode={
                <div className="mb-xl md:mb-0">
                  <Text className="mr-xs">
                    By clicking ‘Split It’, I agree to the
                  </Text>
                  <Button
                    onClick={() => setShowDisclosure(true)}
                    variant="text"
                  >
                    TRUTH IN LENDING DISCLOSURES
                  </Button>
                </div>
              }
              fixedBottom
            />
          </ModalLayout.Content>
        </ModalLayout>
      </ModalV2>
      {showDisclosure && (
        <DisclosureModal
          show={showDisclosure}
          setShow={setShowDisclosure}
          installments={monthlyInstallments}
          balanceIds={balancesIds}
        />
      )}
    </>
  );
}

SplitPaymentModal.propTypes = {
  loading: PropTypes.bool,
  onHide: PropTypes.func,
  balance: PropTypes.object,
  balances: PropTypes.object,
  simulateSplitPayments: PropTypes.func,
  simulation: PropTypes.object,
  items: PropTypes.array,
  handleShowFinanceSuccessModal: PropTypes.func,
  createFinancePlan: PropTypes.func,
  createSplitItPaymentFirst: PropTypes.func,
  billingInfo: PropTypes.object,
};

function mapStateProps(state) {
  return {
    balances: selectBalances(state),
    simulation: selectSimulatePayments(state),
  };
}
const mapDispatchToProps = {
  simulateSplitPayments,
  createFinancePlan,
  createSplitItPaymentFirst,
};

export default connect(mapStateProps, mapDispatchToProps)(SplitPaymentModal);
